import { useEffect, useMemo, useRef, useState } from 'react';
import { type CalendarDate, now, parseZonedDateTime } from '@internationalized/date';
import { getCurrentDate } from '@interxfi/utilities/date/current';
import { usePreferredTimeZone } from '../time-zone-context';
import { useIsDocumentActive } from './dom-active';

export function useCurrentDate(timeZone?: string) {
  const preferredTimeZone = usePreferredTimeZone();
  const usedTimeZone = timeZone || preferredTimeZone;
  const isDocumentActive = useIsDocumentActive();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentDate, setCurrentDate] = useState<CalendarDate>(getCurrentDate(timeZone));

  useEffect(() => {
    function scheduleNextUpdate() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const nowDate = now(usedTimeZone);
      const nextMidnight = nowDate.add({ days: 1 }).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      const msUntilMidnight = Math.max(nextMidnight.toDate().getTime() - nowDate.toDate().getTime(), 0);

      timeoutRef.current = setTimeout(() => {
        setCurrentDate(getCurrentDate(usedTimeZone));

        // recursively schedule next update
        scheduleNextUpdate();
      }, msUntilMidnight);
    }

    if (isDocumentActive) {
      scheduleNextUpdate();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [usedTimeZone, isDocumentActive]);

  return useMemo(() => {
    return {
      date: currentDate,
      zonedDate: parseZonedDateTime(`${currentDate.toString()}T00:00[${usedTimeZone}]`),
      dateAsString: currentDate.toString(),
      timeZone: usedTimeZone,
    };
  }, [currentDate, usedTimeZone]);
}
